import type { TFunction } from 'i18next';
import { DataLoggerDetails } from '../hooks/useDataLoggerDetails';
import { SensorConfig } from '../hooks/useCreateOrUpdateDataLogger';

export const validateFirstTab = (
  formData: Partial<DataLoggerDetails>,
  setErrors: (errors: { [key: string]: string }) => void,
  t: TFunction
): boolean => {
  const newErrors: { [key: string]: string } = {};
  const forbiddenPorts = [21, 22, 23, 25, 53, 80, 110, 143, 443, 3306, 5432, 8080];
  const isValidIPv4 = (ip: string) => {
    return /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ip);
  };
  if (!formData.name) newErrors.name = t('dataLogger.validation.nameRequired', 'Name is required');
  if (!formData.serialNumber) newErrors.serialNumber = t('dataLogger.validation.serialNumberRequired', 'Serial Number is required');
  if (!formData.plcBrand) newErrors.plcBrand = t('dataLogger.validation.plcBrandRequired', 'PLC Brand is required');
  if (!formData.pollingInterval) newErrors.pollingInterval = t('dataLogger.validation.pollingIntervalRequired', 'Polling Interval is required');
  if (!formData.plcModel) newErrors.plcModel = t('dataLogger.validation.plcModelRequired', 'PLC Model is required');
  if (!formData.asset || !formData.asset._id) newErrors.asset = t('dataLogger.validation.assetRequired', 'Asset is required');
  if (!formData.modbusHost) {
    newErrors.modbusHost = t('dataLogger.validation.modbusHostRequired', 'Modbus Host is required');
  } else if (!isValidIPv4(formData.modbusHost)) {
    newErrors.modbusHost = t('dataLogger.validation.invalidIpv4', 'Enter a valid IPv4 address');
  }
  if (!formData.modbusPort) newErrors.modbusPort = t('dataLogger.validation.modbusPortRequired', 'Modbus Port is required');
  if (formData.modbusPort && forbiddenPorts.includes(Number(formData.modbusPort))) {
    newErrors.modbusPort = t('dataLogger.validation.portNotAllowed', 'This port is not allowed for Modbus');
  }
  if (!formData.unitIdReal) newErrors.unitIdReal = t('dataLogger.validation.unitIdRealRequired', 'Unit ID Real is required');
  if (!formData.startAddressReal) newErrors.startAddressReal = t('dataLogger.validation.startAddressRealRequired', 'Start Address Real is required');
  if (!formData.registerCountReal) newErrors.registerCountReal = t('dataLogger.validation.registerCountRealRequired', 'Register Count Real is required');
  if (!formData.unitIdBool) newErrors.unitIdBool = t('dataLogger.validation.unitIdBoolRequired', 'Unit ID Bool is required');
  if (!formData.startAddressBool) newErrors.startAddressBool = t('dataLogger.validation.startAddressBoolRequired', 'Start Address Bool is required');
  if (!formData.registerCountBool) newErrors.registerCountBool = t('dataLogger.validation.registerCountBoolRequired', 'Register Count Bool is required');
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

export const validateSensorConfig = (config: SensorConfig, t: TFunction) => {
  const sensorErrors: { [key: string]: string } = {};
  if (!config.sensor.sensorTag) sensorErrors.sensorTag = t('dataLogger.validation.sensorTagRequired', 'Sensor Tag is required');
  if (!config.sensorType) sensorErrors.sensorType = t('dataLogger.validation.sensorTypeRequired', 'Sensor Type is required');

  if (config.sensorType === 'analog') {
    if (!config.start && config.start !== 0) sensorErrors.start = t('dataLogger.validation.startRegisterRequired', 'Start Register Address is required');
    if (!config.end && config.end !== 0) sensorErrors.end = t('dataLogger.validation.endRegisterRequired', 'End Register Address is required');
    if (!config.type) sensorErrors.type = t('dataLogger.validation.typeRequired', 'Type is required');
    if (!config.wordOrder) sensorErrors.wordOrder = t('dataLogger.validation.wordOrderRequired', 'Word Order is required');
    if (!config.scalingFactor && config.scalingFactor !== 0) sensorErrors.scalingFactor = t('dataLogger.validation.scaleRequired', 'Scale is required');
  }

  if (config.sensorType === 'boolean') {
    if (!config.bitIndex && config.bitIndex !== 0) sensorErrors.bitIndex = t('dataLogger.validation.bitIndexRequired', 'Bit Index is required');
  }

  return sensorErrors;
};

export function validateSensorConfigFrontend(config: any, t: TFunction) {
  const errors: { [key: string]: string } = {};
  if (!config.sensor || typeof config.sensor !== 'object' || !config.sensor._id ) {
    errors['sensor'] = t('dataLogger.validation.sensorRequired', 'Sensor is required');
  }
  if (!config.sensor?.sensorTag) {
    errors['sensorTag'] = t('dataLogger.validation.sensorTagRequiredLower', 'Sensor tag is required');
  }
  if (!config.sensorType) {
    errors['sensorType'] = t('dataLogger.validation.sensorTypeRequiredLower', 'Sensor type is required');
  }
  if (config.sensorType === 'analog') {
    const isTotalizerReference = !!config.referenceSensor && config.referenceSensorPurpose === 'totalizer';
    if (!isTotalizerReference) {
      if (!config.type) errors['type'] = t('dataLogger.validation.typeRequired', 'Type is required');
      if (config.start === undefined || config.start === null || config.start === '') errors['start'] = t('dataLogger.validation.startRegisterRequired', 'Start Register Address is required');
      if (config.end === undefined || config.end === null || config.end === '') errors['end'] = t('dataLogger.validation.endRegisterRequired', 'End Register Address is required');
      if (!config.wordOrder) errors['wordOrder'] = t('dataLogger.validation.wordOrderRequired', 'Word Order is required');
      if (config.scalingFactor === undefined || config.scalingFactor === null || config.scalingFactor === '') errors['scalingFactor'] = t('dataLogger.validation.scalingFactorRequired', 'Scaling Factor is required');
    }
  }
  if (config.sensorType === 'boolean') {
    if (config.bitIndex === undefined || config.bitIndex === null || config.bitIndex === '') errors['bitIndex'] = t('dataLogger.validation.bitIndexRequired', 'Bit Index is required');
  }
  if (config.referenceSensor && !config.referenceSensorPurpose) {
    errors.referenceSensorPurpose = t('dataLogger.validation.purposeRequired', 'Purpose is required when a reference sensor is selected');
  }
  return errors;
}

export function getSensorReferenceResetFields(referenceSensor: any) {
  return {
    referenceSensor,
    start: '',
    end: '',
    type: '',
    wordOrder: '',
    scalingFactor: '',
    function: '',
    modbusMisfireTotalizer: false,
  };
} 